import React, { useState, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Modal,
  Backdrop,
  Fade
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { AppContext } from "../../../context/AppContext";

const CategoryManager = () => {
  const { sections, refreshSections } = useContext(AppContext);

  const [categories, setCategories] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingSection, setEditingSection] = useState("");
  const [editingSequential, setEditingSequential] = useState("");

  const fetchCategories = async (sectionId) => {
    try {
      const response = await fetch(`/categorias/seccion/${sectionId}/`);
      if (!response.ok) throw new Error();
      const data = await response.json();
      setCategories(data);
    } catch {
      setErrorMessage("No se pudieron cargar las categorías.");
    }
  };

  React.useEffect(() => {
    if (selectedSection) {
      fetchCategories(selectedSection);
    } else {
      setCategories([]);
    }
  }, [selectedSection]);

  const handleSubmit = async () => {
    if (!selectedSection || !categoryName.trim()) return;

    if (categories.some((cat) => cat.name.toLowerCase() === categoryName.toLowerCase())) {
      setErrorMessage("Esta categoría ya existe en esta sección.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("section_id", selectedSection);
      formData.append("name", categoryName.trim());

      const response = await fetch("/categorias/agregar/", {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        const res = await response.json();
        setErrorMessage(res.error || "Error al agregar la categoría.");
        return;
      }

      const newCategory = await response.json();
      setCategories((prev) => [...prev, newCategory]);
      setCategoryName("");
      setSuccessMessage("Categoría añadida correctamente.");
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo agregar la categoría.");
    }
  };

  const handleOpenModal = (cat) => {
    setEditingCategory(cat);
    setEditingName(cat.name);
    setEditingSection(selectedSection);
    setEditingSequential(cat.sequential_code || "");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingCategory(null);
    setEditingName("");
    setEditingSection("");
    setEditingSequential("");
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editingName.trim() || !editingSection || !editingSequential.trim()) {
      setErrorMessage("Todos los campos son obligatorios.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("section_id", editingSection);
      formData.append("name", editingName.trim());
      formData.append("sequential_code", editingSequential.trim());

      const response = await fetch(`/categorias/${editingCategory.id}/editar/`, {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        const res = await response.json();
        setErrorMessage(res.error || "Error al editar la categoría.");
        return;
      }

      setSuccessMessage("Categoría editada correctamente.");
      handleCloseModal();
      if (selectedSection) fetchCategories(selectedSection);
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo editar la categoría.");
    }
  };

  const handleDeleteCategory = async () => {
    if (!editingCategory) return;

    try {
      const response = await fetch(`/categorias/${editingCategory.id}/eliminar/`, {
        method: "DELETE"
      });

      if (!response.ok) {
        const res = await response.json();
        setErrorMessage(res.error || "Error al eliminar la categoría.");
        return;
      }

      setSuccessMessage("Categoría eliminada correctamente.");
      handleCloseModal();
      if (selectedSection) fetchCategories(selectedSection);
      await refreshSections();
    } catch {
      setErrorMessage("No se pudo eliminar la categoría.");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Gestión de Categorías
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Selecciona una Sección</InputLabel>
            <Select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              label="Selecciona una Sección"
            >
              {sections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Nombre de la Categoría"
            variant="outlined"
            fullWidth
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            startIcon={<AddIcon />}
            disabled={!selectedSection || !categoryName.trim()}
            sx={{ width: "100%", mt: 2 }}
          >
            Añadir Categoría
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              maxHeight: 250,
              overflowY: "auto",
              p: 2,
              borderRadius: 2,
              backgroundColor: "#f5f5f5"
            }}
          >
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
              Categorías Creadas
            </Typography>

            {categories.length > 0 ? (
              categories.map((category) => (
                <Typography
                  key={category.id}
                  variant="body2"
                  sx={{ mb: 1, cursor: "pointer" }}
                  onClick={() => handleOpenModal(category)}
                >
                  - {category.name}
                </Typography>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: "#666" }}>
                No hay categorías en esta sección.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={openModal}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
              width: 350
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Editar Categoría
            </Typography>

            <TextField
              label="Secuencial"
              variant="outlined"
              fullWidth
              value={editingSequential}
              onChange={(e) => setEditingSequential(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Nombre"
              variant="outlined"
              fullWidth
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Sección</InputLabel>
              <Select
                value={editingSection}
                onChange={(e) => setEditingSection(e.target.value)}
                label="Sección"
              >
                {sections.map((sec) => (
                  <MenuItem key={sec.id} value={sec.id}>
                    {sec.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" justifyContent="space-between">
              <Button color="error" onClick={handleDeleteCategory}>
                Eliminar
              </Button>
              <Button color="primary" onClick={handleSaveEdit}>
                Guardar
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success">{successMessage}</Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={3000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="error">{errorMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryManager;
